/**
 * The onboarding step of the on-device model (models.md §12 "Onboarding", Free): right after the first
 * topic is saved — the moment topic scoring becomes possible — a dialog offers "Sort messages by topic on
 * this device — download the model (135 MB)" with Download and Not now. Nothing downloads without the
 * click (§6.3).
 *
 * Shown at most once per device (a `localStorage` mark; a private window may show it again), only on Free
 * (Pro accounts are pointed at the model by "Use the recommended set-up"), only when the workspace went
 * from no topic to one while the Topics settings were open, and only when an on-device model is switched
 * on and not downloaded here.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import type { EmbeddingCatalogEntry } from '@digester/core';
import { DialogFrame, isDialogRegistered, openDialog, registerDialog, type DialogProps } from '../app/dialogs.js';
import { useEntitlement } from '../app/hooks.js';
import { useEngineQuery } from '../engine-client/EngineProvider.js';
import { qk } from '../engine-client/keys.js';
import { Icon } from '../icons/Icon.js';
import { megabytes } from '../settings/models/catalog.js';
import { isOnDevice, useCatalog, useInstalls, useModels } from '../settings/models/data.js';
import { useModelDownload } from '../settings/models/InstallBlock.js';

export const MODEL_ONBOARDING_KEY = 'digester.model-onboarding.v1';

function seen(): boolean {
  try {
    return localStorage.getItem(MODEL_ONBOARDING_KEY) !== null;
  } catch {
    return false;
  }
}

function markSeen(): void {
  try {
    localStorage.setItem(MODEL_ONBOARDING_KEY, String(Date.now()));
  } catch {
    // Storage blocked: the step may be offered again next time; nothing else depends on it.
  }
}

export function ModelOnboardingDialog({ close, dialogId, entry }: DialogProps & { entry: EmbeddingCatalogEntry }) {
  const download = useModelDownload();
  const size = megabytes(entry.downloadBytes);
  return (
    <DialogFrame title="Sort messages by topic on this device" className="small-dialog onboarding-dialog model-onboarding" dialogId={dialogId} onClose={close}>
      <p>
        Download the model ({size}) and Digester scores every new message against your topics right here — free, private, no tokens — then sorts it into
        Priority, Weak or Dismissed.
      </p>
      <p className="field-help">
        {entry.name}. Once, from Digester’s own server; about {(entry.memoryMb / 1000).toFixed(1)} GB of memory while it works. Until then new messages wait in Inbox.
      </p>
      <div className="dialog-footer">
        <button type="button" className="secondary-button" onClick={close}>
          Not now
        </button>
        <button
          type="button"
          className="primary-button"
          data-action="onboarding-download"
          onClick={() => {
            close();
            download(entry);
          }}
        >
          <Icon name="download" />
          Download ({size})
        </button>
      </div>
    </DialogFrame>
  );
}

export function registerModelOnboardingDialog(): void {
  if (!isDialogRegistered('model-onboarding')) registerDialog<{ entry: EmbeddingCatalogEntry }>('model-onboarding', ModelOnboardingDialog);
}

export function ModelOnboarding() {
  const location = useLocation();
  const entitlement = useEntitlement();
  const topics = useEngineQuery(qk.topics(), (e) => e.topics.list()).data;
  const { models } = useModels();
  const catalog = useCatalog();
  const installs = useInstalls();
  const before = useRef<number | null>(null);
  const shown = useRef(false);
  const pending = useRef(false);
  const onTopics = location.pathname.startsWith('/settings/topics');
  const count = topics?.length ?? null;

  useEffect(() => {
    if (count === null) return;
    const previous = before.current;
    before.current = count;
    // The first topic was just created in Settings → Topics; the dialog follows once everything is known.
    if (previous === 0 && count >= 1 && onTopics) pending.current = true;
    if (!pending.current || shown.current) return;
    if (entitlement && entitlement.plan !== 'free') return;
    if (seen()) return;
    const model = models.find((m) => m.kind === 'onnx-embedding' && m.enabled && m.entitled);
    const entry = catalog?.entries.find((e) => e.id === model?.id);
    if (!model || !isOnDevice(entry) || installs.isLoading) return;
    if (installs.byId.get(model.id)?.phase !== 'absent') return;
    shown.current = true;
    markSeen();
    registerModelOnboardingDialog();
    openDialog('model-onboarding', { entry });
  }, [count, onTopics, entitlement, models, catalog, installs.byId]);

  return null;
}
